import { auth, FormikTextInput, logger, useAlert, useTheme } from "capsule"
import { InputScreen } from "capsule/components/Screens/InputScreen"
import { FormikConfig } from "formik"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Image, Platform, StyleSheet, View } from "react-native"
import FastImage from "react-native-fast-image"
import { Text } from "react-native-paper"
import * as Yup from "yup"

import { images } from "../../assets/images"
import { INPUT_HEIGHT } from "../../features/config/Constants"
import { LoginNS } from "../../features/i18n/constants"

interface ForgotPasswordType {
  email: string
}
const ForgotPasswordScreen = () => {
  const {
    dimensions: { spacing },
    colors: {
      surface: { background, textInput },
    },
  } = useTheme()
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation(LoginNS)
  const { showSnack } = useAlert()

  const { view, inputStyle } = useMemo(
    () => ({
      inputStyle: { backgroundColor: textInput, minHeight: INPUT_HEIGHT },
      view: [
        styles.view,
        { backgroundColor: background, paddingHorizontal: spacing, paddingVertical: spacing * 2 },
      ],
    }),
    [textInput, background, spacing],
  )
  const handleSubmit = async ({ email }: ForgotPasswordType) => {
    setLoading(true)
    try {
      await auth().sendPasswordResetEmail(email)
      showSnack(t("emailSent"))
    } catch (error) {
      logger("Error sending password reset email: ", error)
      showSnack(t("errors.emailSent"))
    } finally {
      setLoading(false)
    }
  }

  const formikConfig: FormikConfig<ForgotPasswordType> = {
    validateOnMount: true,
    onSubmit: handleSubmit,
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email().required(t("errors.emailNeeded")),
    }),
  }

  return (
    <View style={view}>
      {Platform.OS === "web" ? (
        <Image source={images.logo} style={styles.image} />
      ) : (
        <FastImage
          source={images.logo as number}
          style={[styles.image as any]}
          resizeMode={FastImage.resizeMode.contain}
        />
      )}
      <Text style={styles.title}>{t("forgotPassword")}</Text>
      <InputScreen<ForgotPasswordType>
        formLoading={!loading}
        Inputs={
          <FormikTextInput
            name="email"
            changeUnderline={false}
            label={t("email")}
            style={inputStyle}
            testID="input_email"
          />
        }
        buttonLabel={t("sendEmail")}
        formikConfig={formikConfig}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
  title: {
    width: "100%",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 20,
  },
  section: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    gap: 4,
    alignItems: "center",
  },
  image: {
    zIndex: 100,
    width: "50%",
    height: "30%",
    alignSelf: "center",
  },
  link: {
    fontWeight: "semibold",
  },
})

export default ForgotPasswordScreen
